import React, { useEffect, useState } from 'react';
import { Card, Table, Badge, Button, Form, Modal, Spinner, Row, Col, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faInfoCircle, faEdit, faSearch, faImage } from '@fortawesome/free-solid-svg-icons';
import api from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';

const ReturnRequestsAdmin = () => {
    const [returnRequests, setReturnRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [adminNotes, setAdminNotes] = useState('');
    const [customerInstructions, setCustomerInstructions] = useState('');
    const [status, setStatus] = useState('');
    const [processing, setProcessing] = useState(false);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user || !user.is_admin) {
            navigate('/login');
            return;
        }

        fetchReturnRequests();
    }, [user, navigate]);

    const fetchReturnRequests = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/returns');
            setReturnRequests(response.data);
        } catch (err) {
            console.error('İade talepleri getirme hatası:', err);
            setError(err.response?.data?.error || 'İade talepleri getirilemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = (request) => {
        setSelectedRequest(request);
        setAdminNotes(request.admin_notes || '');
        setCustomerInstructions(request.customer_instructions || '');
        setStatus(request.status);
        setShowModal(true);
    };

    const handleUpdateRequest = async () => {
        if (!selectedRequest) return;

        setProcessing(true);
        try {
            const response = await api.put(`/admin/returns/${selectedRequest.id}`, {
                status,
                admin_notes: adminNotes,
                customer_instructions: customerInstructions
            });

            // Başarılı güncelleme
            const updatedRequests = returnRequests.map(req => 
                req.id === selectedRequest.id ? { 
                    ...req, 
                    status, 
                    admin_notes: adminNotes,
                    customer_instructions: customerInstructions
                } : req
            );
            setReturnRequests(updatedRequests);
            setShowModal(false);
            alert('İade talebi başarıyla güncellendi');
        } catch (err) {
            console.error('İade talebi güncelleme hatası:', err);
            alert(err.response?.data?.error || 'İade talebi güncellenirken bir hata oluştu');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <Badge bg="warning">İnceleniyor</Badge>;
            case 'approved':
                return <Badge bg="success">Onaylandı</Badge>;
            case 'rejected':
                return <Badge bg="danger">Reddedildi</Badge>;
            case 'completed':
                return <Badge bg="info">Tamamlandı</Badge>;
            case 'cancelled':
                return <Badge bg="secondary">İptal Edildi</Badge>;
            default:
                return <Badge bg="secondary">Bilinmiyor</Badge>;
        }
    };

    const formatDate = (dateString) => {
        const options = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleDateString('tr-TR', options);
    };

    // İade nedenini Türkçe'ye çeviren fonksiyon
    const getReasonText = (reasonCode) => {
        switch (reasonCode) {
            case 'wrong_product':
                return 'Yanlış ürün gönderildi';
            case 'damaged':
                return 'Ürün hasarlı/arızalı geldi';
            case 'not_as_described':
                return 'Ürün açıklamadaki gibi değil';
            case 'size_issue':
                return 'Ürün boyutu/ölçüsü uygun değil';
            case 'changed_mind':
                return 'Fikrim değişti';
            case 'other':
                return 'Diğer';
            default:
                return reasonCode;
        }
    };

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setSelectedImage('');
    };

    // Filtreleme ve arama
    const filteredRequests = returnRequests.filter(request => {
        // Durum filtresi
        if (filter !== 'all' && request.status !== filter) {
            return false;
        }
        
        // Arama filtresi
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            return (
                request.order_number.toLowerCase().includes(searchLower) ||
                (request.user_email && request.user_email.toLowerCase().includes(searchLower)) ||
                (request.user_name && request.user_name.toLowerCase().includes(searchLower))
            );
        }
        
        return true;
    });

    if (loading) {
        return (
            <div className="admin-loading-container">
                <Spinner animation="border" role="status" variant="primary" />
                <span>İade talepleri yükleniyor...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-error-container">
                <div className="alert alert-danger">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="admin-container">
            <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h3>İade Talepleri Yönetimi</h3>
                    <Button 
                        variant="outline-primary" 
                        onClick={fetchReturnRequests}
                        disabled={loading}
                    >
                        Yenile
                    </Button>
                </Card.Header>
                <Card.Body>
                    <div className="admin-filters mb-3">
                        <div className="row">
                            <div className="col-md-6 mb-2">
                                <Form.Group>
                                    <Form.Label>Durum Filtresi</Form.Label>
                                    <Form.Select 
                                        value={filter} 
                                        onChange={(e) => setFilter(e.target.value)}
                                    >
                                        <option value="all">Tümü</option>
                                        <option value="pending">İnceleniyor</option>
                                        <option value="approved">Onaylandı</option>
                                        <option value="rejected">Reddedildi</option>
                                        <option value="completed">Tamamlandı</option>
                                        <option value="cancelled">İptal Edildi</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-2">
                                <Form.Group>
                                    <Form.Label>Arama</Form.Label>
                                    <div className="input-group">
                                        <Form.Control
                                            type="text"
                                            placeholder="Sipariş no veya müşteri bilgisi ile ara"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <Button variant="outline-secondary">
                                            <FontAwesomeIcon icon={faSearch} />
                                        </Button>
                                    </div>
                                </Form.Group>
                            </div>
                        </div>
                    </div>

                    {filteredRequests.length === 0 ? (
                        <div className="text-center p-4">
                            <p className="mb-0">İade talebi bulunamadı</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Sipariş No</th>
                                        <th>Müşteri</th>
                                        <th>Tarih</th>
                                        <th>Neden</th>
                                        <th>Durum</th>
                                        <th>Görseller</th>
                                        <th>İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRequests.map(request => (
                                        <tr key={request.id}>
                                            <td>{request.id.substring(0, 8)}...</td>
                                            <td>{request.order_number}</td>
                                            <td>
                                                {request.user_name || 'Bilinmiyor'}<br/>
                                                <small>{request.user_email || 'Email yok'}</small>
                                            </td>
                                            <td>{formatDate(request.created_at)}</td>
                                            <td>{request.reason_text || getReasonText(request.reason)}</td>
                                            <td>{getStatusBadge(request.status)}</td>
                                            <td>
                                                {request.images && request.images.length > 0 ? (
                                                    <Button 
                                                        variant="outline-info" 
                                                        size="sm"
                                                        onClick={() => handleShowModal(request)}
                                                    >
                                                        <FontAwesomeIcon icon={faImage} className="me-1" />
                                                        {request.images.length} Görsel
                                                    </Button>
                                                ) : (
                                                    <span className="text-muted">Görsel yok</span>
                                                )}
                                            </td>
                                            <td>
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    onClick={() => handleShowModal(request)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} className="me-1" />
                                                    Düzenle
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* İade Talebi Düzenleme Modalı */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>İade Talebi Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedRequest && (
                        <div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <h5>Sipariş Bilgileri</h5>
                                    <p><strong>Sipariş No:</strong> {selectedRequest.order_number}</p>
                                    <p><strong>Tarih:</strong> {formatDate(selectedRequest.created_at)}</p>
                                    <p><strong>Durum:</strong> {getStatusBadge(selectedRequest.status)}</p>
                                </div>
                                <div className="col-md-6">
                                    <h5>Müşteri Bilgileri</h5>
                                    <p><strong>Ad Soyad:</strong> {selectedRequest.user_name || 'Bilinmiyor'}</p>
                                    <p><strong>E-posta:</strong> {selectedRequest.user_email || 'Bilinmiyor'}</p>
                                </div>
                            </div>

                            <div className="mb-3">
                                <h5>İade Nedeni</h5>
                                <p>{selectedRequest.reason_text || getReasonText(selectedRequest.reason)}</p>
                                {selectedRequest.description && (
                                    <div className="p-3 bg-light rounded">
                                        <h6>Müşteri Açıklaması:</h6>
                                        <p className="mb-0">{selectedRequest.description}</p>
                                    </div>
                                )}
                            </div>

                            {/* Yüklenen görseller */}
                            {selectedRequest.images && selectedRequest.images.length > 0 && (
                                <div className="return-images mb-4">
                                    <h5>Yüklenen Görseller</h5>
                                    <Row className="image-gallery">
                                        {selectedRequest.images.map((imageUrl, index) => (
                                            <Col key={index} xs={6} md={3} className="mb-3">
                                                <div 
                                                    className="return-image-thumbnail" 
                                                    onClick={() => handleImageClick(imageUrl)}
                                                >
                                                    <Image 
                                                        src={imageUrl} 
                                                        thumbnail 
                                                        className="w-100 h-100 object-fit-cover"
                                                        alt={`İade görseli ${index + 1}`}
                                                    />
                                                    <div className="image-overlay">
                                                        <FontAwesomeIcon icon={faImage} />
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}

                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Durum</Form.Label>
                                    <Form.Select 
                                        value={status} 
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        <option value="pending">İnceleniyor</option>
                                        <option value="approved">Onaylandı</option>
                                        <option value="rejected">Reddedildi</option>
                                        <option value="completed">Tamamlandı</option>
                                        <option value="cancelled">İptal Edildi</option>
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Admin Notları</Form.Label>
                                    <Form.Control 
                                        as="textarea" 
                                        rows={4} 
                                        value={adminNotes}
                                        onChange={(e) => setAdminNotes(e.target.value)}
                                        placeholder="İade talebi hakkında notlar ekleyin"
                                    />
                                    <Form.Text className="text-muted">
                                        Bu notlar müşteriye gösterilecektir.
                                    </Form.Text>
                                </Form.Group>

                                {status === 'approved' && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Müşteri Talimatları</Form.Label>
                                        <Form.Control 
                                            as="textarea" 
                                            rows={4} 
                                            value={customerInstructions}
                                            onChange={(e) => setCustomerInstructions(e.target.value)}
                                            placeholder="Müşteriye iletilecek iade talimatlarını girin"
                                        />
                                        <Form.Text className="text-muted">
                                            Bu talimatlar, iade talebi onaylandığında müşteriye gösterilecektir.
                                        </Form.Text>
                                    </Form.Group>
                                )}
                            </Form>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        İptal
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleUpdateRequest}
                        disabled={processing}
                    >
                        {processing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Güncelleniyor...
                            </>
                        ) : (
                            'Güncelle'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Görsel Önizleme Modalı */}
            <Modal show={showImageModal} onHide={closeImageModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Görsel Önizleme</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <Image src={selectedImage} fluid alt="İade görseli" className="modal-image" />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeImageModal}>
                        Kapat
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ReturnRequestsAdmin; 